import { useEffect, useRef } from 'react';
import { DayButtonProps, DayPicker } from 'react-day-picker';
import { ja } from 'react-day-picker/locale';
import { formatDate } from '../../../utils/common/date';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '../Icons';
import styles from './styles.module.scss';

type Props = {
  captionLayout: 'label' | 'dropdown';
  selected: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  fromDate?: Date;
  toDate?: Date;
};

/**
 * react-day-picker を基にしたカスタムカレンダーコンポーネント
 * 日本語ロケールと単一日選択をサポートします
 * @param props - コンポーネントのプロパティ
 * @returns Calendar コンポーネント
 */
export const Calendar: React.FC<Props> = ({
  captionLayout,
  selected,
  onSelect,
  fromDate,
  toDate,
}) => {
  return (
    <DayPicker
      mode='single'
      selected={selected}
      onSelect={onSelect}
      showOutsideDays
      navLayout='around'
      locale={ja}
      captionLayout={captionLayout}
      disabled={{ before: fromDate, after: toDate }}
      formatters={{
        formatCaption: date => formatDate({ date, formatType: 'yyyy年M月' }),
      }}
      classNames={styles}
      components={{
        Chevron: ({ className, orientation }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={className} />;
          }

          if (orientation === 'right') {
            return <ChevronRightIcon />;
          }

          return <ChevronDownIcon />;
        },
        DayButton: CalendarDayButton,
      }}
    />
  );
};

/**
 * カスタム日付ボタンコンポーネント
 * フォーカス管理とデータ属性の追加を行います
 * - DayButtonはElementしか受け取らないため、FCではなくJSX.Elementを返す
 *
 * @param props - DayButton コンポーネントのプロパティ
 * @returns カスタマイズされた日付ボタン
 */
const CalendarDayButton = ({
  className,
  day,
  modifiers,
  ...props
}: DayButtonProps): React.JSX.Element => {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString().slice(0, 10)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
          ? 'true'
          : undefined
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={className}
      {...props}
    />
  );
};
